import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import ToolShell from "./ToolShell";
import { Panel, TextField, SelectField, NumField, Stat } from "./ToolUI";
import { num, saveResult, loadDraft, saveDraft } from "./toolkit";

const EMPTY = {
  date: new Date().toISOString().slice(0, 10), shift: "A — Day", area: "", outgoing: "", incoming: "",
  incident: "No", safety: "", target: "", actual: "", scrap: "", quality: "",
  equipment: [{ machine: "", status: "Running", notes: "" }],
  actions: [{ task: "", owner: "", due: "" }], staffing: "", notes: "",
};
const STATUS_TONE = { Running: "text-emerald-400", Limited: "text-amber-400", Down: "text-red-400" };

const Rows = ({ rows, cols, onChange, add, testPrefix }) => (
  <div className="space-y-2">
    {rows.map((row, i) => (
      <div key={i} className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
        {cols.map((c) => c.options ? (
          <SelectField key={c.k} label={i === 0 ? c.label : ""} value={row[c.k]} options={c.options}
            onChange={(v) => onChange(rows.map((r, j) => (j === i ? { ...r, [c.k]: v } : r)))} testid={`${testPrefix}-${c.k}-${i}`} />
        ) : (
          <TextField key={c.k} label={i === 0 ? c.label : ""} value={row[c.k]}
            onChange={(v) => onChange(rows.map((r, j) => (j === i ? { ...r, [c.k]: v } : r)))} testid={`${testPrefix}-${c.k}-${i}`} />
        ))}
        <button type="button" disabled={rows.length === 1} onClick={() => onChange(rows.filter((_, j) => j !== i))}
          className="h-11 px-3 border border-border rounded-sm text-muted-foreground hover:text-red-400 disabled:opacity-30 no-print" data-testid={`${testPrefix}-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    ))}
    <button type="button" onClick={add} className="text-sm text-primary inline-flex items-center hover:underline no-print" data-testid={`${testPrefix}-add`}><Plus className="w-4 h-4 mr-1" />Add row</button>
  </div>
);

export default function ShiftHandover() {
  const [h, setH] = useState(() => loadDraft("handover", EMPTY));
  const [touched, setTouched] = useState(false);
  const up = (patch) => { setTouched(true); setH((s) => ({ ...s, ...patch })); };
  const t = (k) => (v) => up({ [k]: v });
  const attainment = num(h.target) ? (num(h.actual) / num(h.target)) * 100 : 0;
  const down = h.equipment.filter((e) => e.status === "Down").length;
  const openActions = h.actions.filter((a) => a.task).length;

  useEffect(() => { saveDraft("handover", h); }, [h]);
  useEffect(() => { if (touched && num(h.target)) saveResult("handover", { attainment }); }, [touched, attainment, h.target]);

  const share = num(h.target) ? {
    slug: "shift-handover", tool: "Shift Attainment", scoreText: `${attainment.toFixed(0)}%`, buttonLabel: "Share shift result",
    label: `${num(h.actual).toLocaleString()} of ${num(h.target).toLocaleString()} units · ${h.incident === "No" ? "zero safety incidents" : "safety incident logged"}`,
    bar: { value: attainment, target: 100, targetLabel: "Plan 100%" },
    tagline: "Clean handovers, every shift, with RuleAI",
    caption: `Shift ${h.shift} hit ${attainment.toFixed(0)}% of plan. Handover logged with RuleAI Systems.`,
  } : null;

  return (
    <ToolShell toolKey="handover" overline="Free tool · Shift management" title="Shift Handover Log"
      intro="A structured handover so nothing gets lost between shifts: safety, output, quality, equipment status and open actions. Fill it in, print it, or save as PDF. Drafts save in this browser."
      share={share} results={{ attainment, ...h }}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="handover-summary">
        <Stat label="Attainment" value={num(h.target) ? `${attainment.toFixed(0)}%` : "—"} tone={attainment >= 100 ? "text-emerald-400" : attainment ? "text-amber-400" : ""} testid="handover-attainment" />
        <Stat label="Safety" value={h.incident === "No" ? "Clear" : "Incident"} tone={h.incident === "No" ? "text-emerald-400" : "text-red-400"} testid="handover-safety-status" />
        <Stat label="Machines down" value={down} tone={down ? "text-red-400" : ""} testid="handover-down-count" />
        <Stat label="Open actions" value={openActions} testid="handover-open-actions" />
      </div>
      <Panel overline="01" title="Shift details" testid="handover-details">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <TextField type="date" label="Date" value={h.date} onChange={t("date")} testid="handover-date" />
          <SelectField label="Shift" value={h.shift} onChange={t("shift")} options={["A — Day", "B — Swing", "C — Night"]} testid="handover-shift" />
          <TextField label="Line / area" value={h.area} onChange={t("area")} testid="handover-area" />
          <TextField label="Outgoing lead" value={h.outgoing} onChange={t("outgoing")} testid="handover-outgoing" />
          <TextField label="Incoming lead" value={h.incoming} onChange={t("incoming")} testid="handover-incoming" />
        </div>
      </Panel>
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel overline="02" title="Safety" testid="handover-safety">
          <div className="space-y-4">
            <SelectField label="Any incidents or near misses?" value={h.incident} onChange={t("incident")} options={["No", "Near miss", "Incident"]} testid="handover-incident" />
            <TextField label="Details / hazards for next shift" value={h.safety} onChange={t("safety")} multiline testid="handover-safety-notes" />
          </div>
        </Panel>
        <Panel overline="03" title="Production & quality" testid="handover-production">
          <div className="grid grid-cols-3 gap-3">
            <NumField label="Target" value={h.target} onChange={t("target")} testid="handover-target" />
            <NumField label="Actual" value={h.actual} onChange={t("actual")} testid="handover-actual" />
            <NumField label="Scrap" value={h.scrap} onChange={t("scrap")} testid="handover-scrap" />
          </div>
          <div className="mt-4"><TextField label="Quality issues / holds" value={h.quality} onChange={t("quality")} multiline testid="handover-quality" /></div>
        </Panel>
      </div>
      <Panel overline="04" title="Equipment status" testid="handover-equipment">
        <Rows rows={h.equipment} testPrefix="handover-eq" onChange={(equipment) => up({ equipment })}
          add={() => up({ equipment: [...h.equipment, { machine: "", status: "Running", notes: "" }] })}
          cols={[{ k: "machine", label: "Machine" }, { k: "status", label: "Status", options: ["Running", "Limited", "Down"] }, { k: "notes", label: "Notes" }]} />
        <div className="flex flex-wrap gap-3 mt-4 text-xs font-mono-plex">
          {h.equipment.filter((e) => e.machine).map((e, i) => <span key={i} className={STATUS_TONE[e.status]}>● {e.machine}: {e.status}</span>)}
        </div>
      </Panel>
      <Panel overline="05" title="Open actions for next shift" testid="handover-actions">
        <Rows rows={h.actions} testPrefix="handover-action" onChange={(actions) => up({ actions })}
          add={() => up({ actions: [...h.actions, { task: "", owner: "", due: "" }] })}
          cols={[{ k: "task", label: "Task" }, { k: "owner", label: "Owner" }, { k: "due", label: "Due / priority" }]} />
      </Panel>
      <Panel overline="06" title="Staffing & notes" testid="handover-notes">
        <div className="grid lg:grid-cols-2 gap-4">
          <TextField label="Staffing (absences, overtime, training)" value={h.staffing} onChange={t("staffing")} multiline testid="handover-staffing" />
          <TextField label="Anything else the next shift must know" value={h.notes} onChange={t("notes")} multiline testid="handover-notes-input" />
        </div>
        <div className="print-only mt-8 grid grid-cols-2 gap-8 text-sm"><div className="border-t border-border pt-2">Outgoing signature</div><div className="border-t border-border pt-2">Incoming signature</div></div>
      </Panel>
      <button type="button" onClick={() => setH(EMPTY)} className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground hover:text-foreground no-print" data-testid="handover-reset">Clear form</button>
    </ToolShell>
  );
}
